import { useActiveUserContext, useAssignedAppTypeContext, useDataRequestPermissionsContext } from '@vivli/core/infrastructure/context';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';

export function useDataRequestDefaultTabHook() {
    const { userIsContributorOrIRPApprover, shouldHideReviewers } = useDataRequestPermissionsContext();
    const { user: activeUser, hasPermission, orgHasPermission } = useActiveUserContext();
    const { isAmr } = useAssignedAppTypeContext();
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);

    const getAmrRowClick = (isDraft: boolean, isContributorOrIRPApprover: boolean, isOwner: boolean) => {
        const isContributorIrpOrNotDraft = isContributorOrIRPApprover || !isDraft;
        const secondaryAmrPath = isContributorIrpOrNotDraft ? 'printview' : 'request';
        //TODO PERMFE - redo with permissions+
        const amrTabPath = isVivliAdmin && !isOwner ? 'requestprintview' : secondaryAmrPath;
        return amrTabPath;
    };

    //DEV-988: which of the two data request views a request belongs in. The draft form nav and the
    //read-only tabs nav are separate components selected purely by this url segment, so any entry
    //point that lands on the wrong one shows the wrong tab list until the route is recomputed.
    const getCanonicalRouteBase = (dataRequest: IDataRequest, organizations: IOrganization[]) => {
        const isDraft = dataRequest.status === DataRequestStatusEnum.Draft;
        if (!isDraft) {
            return 'admindatarequests';
        }

        const isOwner = dataRequest.userId === activeUser.userId;
        if (isOwner) {
            return 'myDataRequestDetails';
        }

        //TODO PERMFE - redo with permissions+
        const orgAdmin = activeUser.orgMemberships?.some((om) =>
            organizations.some((o) => orgHasPermission(o.id, PermissionsEnum.ProviderActAdv))
        );
        if (!isVivliAdmin && !orgAdmin) {
            return 'myDataRequestDetails';
        }

        return 'admindatarequests';
    };

    const getDefaultTabRoute = (dataRequest: IDataRequest, organizations: IOrganization[], requestStatus: string) => {
        const teamMember = dataRequest.researchTeam.researchTeamDetails.find((x) => x.userId === activeUser.userId);
        const shouldHideReviews = shouldHideReviewers(dataRequest, organizations);
        const isContributorOrIRPApprover = userIsContributorOrIRPApprover(dataRequest, organizations);
        const isDraft = dataRequest.status === DataRequestStatusEnum.Draft;
        const isOwner = dataRequest.userId === activeUser.userId;
        //TODO PERMFE - redo with permissions+
        const navTabPath = isDraft && (teamMember || isVivliAdmin || isOwner) && !shouldHideReviews ? 'researchTeam' : 'chat';
        const amrTabPath = getAmrRowClick(isDraft, isContributorOrIRPApprover, isOwner);
        const chosenTab = isAmr ? amrTabPath : navTabPath;
        const chosenRoute = getCanonicalRouteBase(dataRequest, organizations);

        //the router exposes both ':status/:requestId' and ':requestId', so drop the segment rather
        //than emitting '/undefined/' when a caller has no status to give (DEV-988)
        const statusSegment = requestStatus ? `${requestStatus}/` : '';

        return `/${chosenRoute}/${statusSegment}${dataRequest.id}/${chosenTab}`;
    };

    return {
        getDefaultTabRoute,
        getCanonicalRouteBase,
    };
}
